import { GetServerSidePropsContext } from 'next';
import Router from 'next/router';

import { deleteJwt, getJwt, setJwt } from '@/common/cookie/jwt';
import { wrapGetServerSideProps } from '@/common/next';
import { INTERNAL_SERVER_ERROR, UNAUTHORIZED } from '@/constants/commons/statusCode';
import { ERROR404_URL, ERROR409_URL, ERROR500_URL, LOGIN_URL, RESERVATION_LIST_URL } from '@/constants/commons/url';
import { LoginTypeEnum } from '@/hooks/pages/login/useLogin';
import { CheckLoggedInAPI } from '@/infrastructures/api/concierges/CheckLoggedInAPI';

export const createRefererParameter = (url?: string) => {
  let referer = url ?? location.href;
  if (referer.includes(LOGIN_URL) || /\/$/.test(referer)) {
    referer = RESERVATION_LIST_URL;
  }
  return `&referer=${encodeURIComponent(referer)}`;
};

/**認証失敗時のリダイレクト先*/
const REDIRECT_URL_NO_SESSION = (referer?: string) =>
  `${LOGIN_URL}?type=${LoginTypeEnum.NO_SESSION}&${createRefererParameter(referer)}`;
const REDIRECT_URL_SESSIONS_EXPIRED = (referer: string) =>
  `${LOGIN_URL}?type=${LoginTypeEnum.SESSION_EXPIRED}&${createRefererParameter(referer)}`;
const REDIRECT_URL_500_ERROR = `/${INTERNAL_SERVER_ERROR}`;

export type TPagePropsForJwtAuthentication = {
  conciergeId: number | null;
};

/** 画面遷移時に行うJWTの認証 */
export const getServerSidePropsForAuthentication = wrapGetServerSideProps(
  async (
    context: GetServerSidePropsContext,
  ): Promise<{ props?: TPagePropsForJwtAuthentication; redirect?: TRedirect }> => {
    let conciergeId: number | null = null;
    const referer = context.resolvedUrl ?? RESERVATION_LIST_URL;

    // JWTがCookieにない場合は未ログイン状態としてログイン画面に遷移
    if (!getJwt(context)) {
      return redirect(`${REDIRECT_URL_NO_SESSION(referer)}`);
    } else {
      const result = await CheckLoggedInAPI.post(context);
      // JWTの期限切れの場合は再度LWTログイン画面に遷移
      if (result.status === UNAUTHORIZED) {
        deleteJwt(context);
        return redirect(REDIRECT_URL_SESSIONS_EXPIRED(referer));
      } else if (result.isError || !result.data) {
        return redirect(REDIRECT_URL_500_ERROR);
      } else {
        setJwt(result.data.jwt, context);
        conciergeId = result.data.conciergeId;
      }
    }

    return { props: { conciergeId } };
  },
);

/**
 * JWTがCookieにない場合は未ログイン状態としてログイン画面に遷移
 *  jwt OK -> true, jwt NG -> false
 */
export const checkJwt = (): boolean => {
  // クライアントサイドの場合のみチェックする
  if (global.window === undefined) return true;
  // ログイン画面、エラー画面の場合は、ログイン画面に遷移しない
  const pathname = window.location.href;
  if ([LOGIN_URL, ERROR404_URL, ERROR409_URL, ERROR500_URL].some((url) => pathname.includes(url))) return true;
  if (!getJwt()) {
    Router.push(REDIRECT_URL_NO_SESSION());
    return false;
  }
  return true;
};

/**
 * @private
 * @param url
 */
export const redirect = (url: string): { redirect: TRedirect } => {
  return {
    redirect: {
      destination: url,
      permanent: false,
    },
  };
};

export type TRedirect = {
  destination: string;
  permanent: boolean;
};
